import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, Calendar, Mail, Phone, MapPin, Download } from 'lucide-react';

const Admin = () => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    fetchParticipants();
    fetchCount();
  }, []);

  const locationStats = useMemo(() => {
    const counts = participants.reduce((acc, p) => {
      const key = (p.location || 'Unspecified').toString().trim() || 'Unspecified';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const max = entries.length ? Math.max(...entries.map(([, v]) => v)) : 0;
    return { entries, max };
  }, [participants]);

  const ageStats = useMemo(() => {
    const buckets = {
      '18-21': 0,
      '22-25': 0,
      '26-29': 0,
      Other: 0,
    };
    participants.forEach((p) => {
      const ageNum = Number(p.age);
      if (ageNum >= 18 && ageNum <= 21) buckets['18-21'] += 1;
      else if (ageNum >= 22 && ageNum <= 25) buckets['22-25'] += 1;
      else if (ageNum >= 26 && ageNum <= 29) buckets['26-29'] += 1;
      else buckets.Other += 1;
    });
    const entries = Object.entries(buckets);
    const max = entries.length ? Math.max(...entries.map(([, v]) => v)) : 0;
    return { entries, max };
  }, [participants]);

  const fetchParticipants = async () => {
    try {
      const response = await fetch('/api/participants');
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
      } else {
        setError('Failed to fetch participants');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const response = await fetch('/api/participants/count');
      if (response.ok) {
        const data = await response.json();
        setCount(data.count);
      }
    } catch (err) {
      console.error('Failed to fetch count:', err);
    }
  };


  const exportToCSV = () => {
    const headers = ['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Age', 'Location', 'Interest', 'Message', 'Created At (ET)'];
    const csvContent = [
      headers.join(','),
      ...participants.map(p => [
        p.id,
        `"${p.firstName}"`,
        `"${p.lastName}"`,
        `"${p.email}"`,
        `"${p.phone || ''}"`,
        p.age,
        `"${p.location || ''}"`,
        `"${p.interest || ''}"`,
        `"${p.message || ''}"`,
        `"${(() => {
          try {
            const date = new Date(p.createdAt);
            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString('en-US', { timeZone: 'America/New_York' });
          } catch (e) {
            return 'Invalid Date';
          }
        })()}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `participants-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container" style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading participants...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="container" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ color: 'red' }}>Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Visualizations */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Overview</h2>
            <div className="grid grid-3" style={{ marginTop: '1.5rem' }}>
              <div className="card text-center">
                <Users size={32} style={{ color: '#1E40AF', marginBottom: '0.5rem' }} />
                <h3 style={{ marginBottom: '0.25rem' }}>Total Applications</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>{count}</div>
              </div>
              <div className="card text-center">
                <Calendar size={32} style={{ color: '#1E40AF', marginBottom: '0.5rem' }} />
                <h3 style={{ marginBottom: '0.25rem' }}>Today</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
                  {participants.filter(p => {
                    try {
                      const today = new Date();
                      const created = new Date(p.createdAt);
                      return !isNaN(created.getTime()) && created.toDateString() === today.toDateString();
                    } catch (e) { return false; }
                  }).length}
                </div>
              </div>
              <div className="card text-center">
                <MapPin size={32} style={{ color: '#1E40AF', marginBottom: '0.5rem' }} />
                <h3 style={{ marginBottom: '0.25rem' }}>Unique Locations</h3>
                <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
                  {new Set(participants.map(p => (p.location || 'Unspecified').toString().trim() || 'Unspecified')).size}
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-2" style={{ marginTop: '1.5rem' }}>
              {/* Top Locations - SVG Bar Chart */}
              <div className="card">
                <h3 style={{ marginBottom: '0.75rem', color: '#1E40AF' }}>Top Locations</h3>
                <svg viewBox="0 0 600 220" width="100%" height="220" role="img" aria-label="Top locations bar chart">
                  {(() => {
                    const marginL = 160; // label width
                    const marginR = 40;
                    const barH = 24;
                    const gap = 12;
                    const startY = 20;
                    const maxW = 600 - marginL - marginR;
                    const rows = locationStats.entries.slice(0, 6);
                    return rows.map(([name, value], i) => {
                      const y = startY + i * (barH + gap);
                      const w = locationStats.max ? (value / locationStats.max) * maxW : 0;
                      return (
                        <g key={name}>
                          <text x={0} y={y + barH - 6} fill="#555" fontSize="12">{name}</text>
                          <rect x={marginL} y={y} width={maxW} height={barH} fill="#edf2f7" rx="6" />
                          <rect x={marginL} y={y} width={w} height={barH} fill="#1E40AF" rx="6" />
                          <text x={marginL + w + 8} y={y + barH - 6} fill="#333" fontSize="12" fontWeight="600">{value}</text>
                        </g>
                      );
                    });
                  })()}
                </svg>
              </div>

              {/* Age Distribution - SVG Donut Chart */}
              <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '380px' }}>
                  <h3 style={{ marginBottom: '0.75rem', color: '#1E40AF' }}>Age Distribution</h3>
                  {(() => {
                    const total = ageStats.entries.reduce((a, [, v]) => a + v, 0) || 1;
                    const circumference = 2 * Math.PI * 70; // r=70
                    let offset = 0;
                    const colors = {
                      '18-21': '#1E40AF',
                      '22-25': '#3B82F6',
                      '26-29': '#48bb78',
                      'Other': '#ed8936'
                    };
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="Age distribution donut chart">
                          <circle cx="100" cy="100" r="70" fill="none" stroke="#edf2f7" strokeWidth="20" />
                          {ageStats.entries.map(([label, value]) => {
                            const frac = value / total;
                            const len = frac * circumference;
                            const dashArray = `${len} ${circumference - len}`;
                            const el = (
                              <circle key={label}
                                cx="100" cy="100" r="70" fill="none"
                                stroke={colors[label] || '#999'} strokeWidth="20"
                                strokeDasharray={dashArray}
                                strokeDashoffset={-offset}
                                transform="rotate(-90 100 100)" />
                            );
                            offset += len;
                            return el;
                          })}
                          <text x="100" y="105" textAnchor="middle" fontSize="14" fill="#333" fontWeight="700">{total}</text>
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          {ageStats.entries.map(([label, value]) => (
                            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#555', fontSize: '0.95rem' }}>
                              <span style={{ width: '10px', height: '10px', background: (colors[label] || '#999'), borderRadius: '2px' }} />
                              <span style={{ width: '64px' }}>{label}</span>
                              <span style={{ fontWeight: 600 }}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Hero Section */}
      <section className="hero-small" style={{ 
        paddingTop: '80px', 
        paddingBottom: '10px',
        backgroundImage: 'linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="section-title">Admin Dashboard</h1>
            <p className="section-subtitle">
              Manage participant applications and view study progress
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <Users size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
              <span className="stat-number">{count}</span>
              <div className="stat-label">Total Applications</div>
            </div>
            <div className="stat-item">
              <Calendar size={48} style={{ color: '#1E40AF', marginBottom: '1rem' }} />
              <span className="stat-number">{participants.filter(p => {
                try {
                  const today = new Date();
                  const created = new Date(p.createdAt);
                  return !isNaN(created.getTime()) && created.toDateString() === today.toDateString();
                } catch (e) {
                  return false;
                }
              }).length}</span>
              <div className="stat-label">Today's Applications</div>
            </div>
          </div>
        </div>
      </section>


      {/* Participants Table */}
      <section className="section" style={{ background: '#f8fafc' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h2 className="section-title" style={{ marginBottom: 0 }}>Participant Applications</h2>
            <button 
              onClick={exportToCSV}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Download size={16} />
              Export CSV
            </button>
          </div>

          <div className="card" style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Contact</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Age</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Location</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Interest</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Applied</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((participant) => (
                  <motion.tr 
                    key={participant.id}
                    style={{ borderBottom: '1px solid #e2e8f0' }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600' }}>
                        {participant.firstName} {participant.lastName}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Mail size={14} />
                          <span>{participant.email}</span>
                        </div>
                        {participant.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Phone size={14} />
                            <span>{participant.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>{participant.age}</td>
                    <td style={{ padding: '1rem' }}>
                      {participant.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <MapPin size={14} />
                          <span>{participant.location}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '1rem' }}>{participant.interest || '-'}</td>
                    <td style={{ padding: '1rem' }}>
                      {(() => {
                        try {
                          const date = new Date(participant.createdAt);
                          return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
                        } catch (e) {
                          return 'Invalid Date';
                        }
                      })()}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Admin;
